import { apiQuery } from "utils/queries";
import { api } from "../restUtil";
import type { InstructorAvailableSessions, InstructorProfile } from "./model";
import type { UserTag } from "services/users/model";

type FetchInstructorsParams = {
  from?: number;
  to?: number;
  step?: number;
  sort?: "name";
};

type TimezoneInfo = {
  id: string;
  utc_offset: string;
};

type CityCountryInfo = {
  city: string | null;
  country: string | null;
};

export type InstructorParticipant = {
  id: string;
  profile: {
    first_name: string | null;
    last_name?: string | null;
    email: string | null;
    timezone: TimezoneInfo;
    address: CityCountryInfo;
  };
};

export type InstructorSummaryResponse = {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  timezone: TimezoneInfo;
} & CityCountryInfo & {
    last_seen: string | null;
    number_of_lessons: number;
    avatar: string;
  };

type FetchInstructorsResponse = {
  list: InstructorSummaryResponse[];
};

export function fetchInstructors(args: FetchInstructorsParams | null) {
  return api.get<FetchInstructorsResponse>("/instructors", args);
}

export async function getPrimaryInstructorForStudent() {
  const resp = await api.get<InstructorSummaryResponse | null>("/instructors/primary");

  // TODO change the server to return required response structure
  if (!resp) {
    return null;
  }
  if (resp.last_name === null) {
    throw new Error("invalid response structure: expected primary instructor last_name");
  }

  return {
    id: resp.id,
    profile: {
      email: resp.email,
      first_name: resp.first_name,
      //later (when student will be able to choose from the list of instructors) we will left >=1 letters of last_name,
      //to be sure that student can differentiate instructors
      last_name: resp.last_name.charAt(0),
      timezone: {
        id: resp.timezone.id,
        utc_offset: resp.timezone.utc_offset,
      },
      address: {
        city: resp.city,
        country: resp.country,
      },
    },
  };
}

type FetchInstructorResponse = {
  id: string;
  avatar: string;
  instructor_id: string;
  last_seen: string | null;
  degrees: {
    title: string;
    university: {
      name: string;
      city: string | null;
      country: string | null;
    };
  }[];
  profile: {
    first_name: string | null;
    last_name: string | null;
    nickname: string | null;
    email: string | null;
    native_language: string | null;
    timezone: TimezoneInfo;
    address: {
      address1: string | null;
      address2: string | null;
      state: string | null;
      zipcode: string | null;
    } & CityCountryInfo;
  };
};

export async function fetchInstructor(id: string) {
  return api.get<FetchInstructorResponse>(`/instructor/${id}`);
}

export function toInstructorParticipant(
  resp: FetchInstructorResponse
): InstructorParticipant {
  return {
    id: resp.id,
    profile: {
      first_name: resp.profile.first_name,
      email: resp.profile.email,
      timezone: {
        id: resp.profile.timezone.id,
        utc_offset: resp.profile.timezone.utc_offset,
      },
      address: {
        city: resp.profile.address.city,
        country: resp.profile.address.country,
      },
    },
  };
}

export async function fetchInstructorAvailableSessions(studentId: string) {
  return api.get<InstructorAvailableSessions>(`/instructor/${studentId}/sessions`);
}

export function fetchInstructorProfile() {
  return api.get<InstructorProfile>("/instructor");
}

export const instructorProfileQuery = apiQuery<InstructorProfile>({
  key: "/instructor",
});

type InstructorTagsResponse = {
  tags: UserTag[];
};

export function fetchInstructorTags() {
  return api.get<InstructorTagsResponse>("/instructor/tags");
}

export const myInstructorTagsQuery = apiQuery<InstructorTagsResponse>({
  key: "/instructor/tags",
});

type UpdateInstructorCapacityPayload = {
  max_capacity: string | number;
};

export function updateInstructorCapacity(maxCapacity: string | number) {
  return api.patch<UpdateInstructorCapacityPayload, ApiStatusResponse>("/instructor", {
    max_capacity: maxCapacity,
  });
}

type AddTagPayload = {
  instructor_id?: string;
  tags: string[];
};

export function addTag(tags: string[], instructorId?: string) {
  const payload = {
    instructor_id: instructorId,
    tags,
  };

  return api.post<AddTagPayload, ApiStatusResponse>("/instructor/tags", payload);
}

type RemoveTagPayload = {
  instructor_id?: string;
  tags: string[];
};

export function removeTag(tags: string[], instructorId?: string) {
  const payload = {
    instructor_id: instructorId,
    tags,
  };

  return api.delete<RemoveTagPayload, void>("/instructor/tags", payload);
}

type ApiStatusResponse = {
  code: string;
  message: string;
  title: string;
};
